import { z } from "zod";

/**
 * Globální role uživatele ve Factory.
 * Project-scoped role viz ProjectMemberRole — obě vrstvy se kombinují.
 * MANAGER smí zakládat projekty; při založení se stává jejich členem (MANAGER).
 */
export const UserRole = z.enum(["OWNER", "MANAGER", "MEMBER", "CLIENT"]);
export type UserRole = z.infer<typeof UserRole>;

export const ProjectMemberRole = z.enum(["MANAGER", "COLLABORATOR", "CLIENT_VIEWER"]);
export type ProjectMemberRole = z.infer<typeof ProjectMemberRole>;

/** Lifecycle projektu — mění chování AI (policy-driven, spec §10). */
export const ProjectLifecycle = z.enum([
  "DISCOVERY",
  "ACTIVE_DEV",
  "LAUNCH_PREP",
  "LIVE",
  "MAINTENANCE",
  "PAUSED",
  "ARCHIVED",
]);
export type ProjectLifecycle = z.infer<typeof ProjectLifecycle>;

/** Business priorita projektu — vstup do Effective Priority. */
export const ProjectPriority = z.enum(["LOW", "NORMAL", "HIGH", "CRITICAL"]);
export type ProjectPriority = z.infer<typeof ProjectPriority>;

/** Typ projektu (šablony přijdou ve Fázi 2 — enum drží základní sadu). */
export const ProjectKind = z.enum(["SAAS", "CLIENT", "INTERNAL", "OTHER"]);
export type ProjectKind = z.infer<typeof ProjectKind>;


/**
 * Plán vývoje — checklist derivovaný z Knowledge (modul ROADMAP, záložka
 * PLÁN VÝVOJE). Udržuje ho AI: extrakce podkladů a doplňovací kola; web ho
 * čte jako běžný knowledge dokument na téhle adrese.
 */
export const PLAN_DOC_FOLDER = "Product";
export const PLAN_DOC_TITLE = "Plán vývoje";
export const PLAN_DOC_SLUG = "plan-vyvoje";

/** Kdo akci provedl — jádro auditu (spec §59). */
export const ActorType = z.enum(["HUMAN", "AI", "WORKER", "SYSTEM"]);
export type ActorType = z.infer<typeof ActorType>;

// ---------------------------------------------------------------------------
// Fáze 2 — Projects & Knowledge
// ---------------------------------------------------------------------------

/** Druh uploadu: SOURCE = raw podklad projektu, DESIGN = design artefakt aplikace. */
export const UploadKind = z.enum(["SOURCE", "DESIGN"]);
export type UploadKind = z.infer<typeof UploadKind>;

/** Stav zpracování uploadu do Knowledge (spec §18–19). */
export const UploadStatus = z.enum(["NEW", "PROCESSED", "ARCHIVED"]);
export type UploadStatus = z.infer<typeof UploadStatus>;

/** Stav knowledge dokumentu — SUPERSEDED znamená nahrazen novější pravdou. */
export const KnowledgeDocStatus = z.enum(["ACTIVE", "SUPERSEDED", "ARCHIVED"]);
export type KnowledgeDocStatus = z.infer<typeof KnowledgeDocStatus>;

/** Původ znalosti/revize (provenance, spec §21). */
export const KnowledgeSourceType = z.enum(["UPLOAD", "HUMAN", "AI", "DECISION", "SYSTEM"]);
export type KnowledgeSourceType = z.infer<typeof KnowledgeSourceType>;

export const ConflictStatus = z.enum(["OPEN", "RESOLVED"]);
export type ConflictStatus = z.infer<typeof ConflictStatus>;

export const ConflictChoice = z.enum(["A", "B", "CUSTOM"]);
export type ConflictChoice = z.infer<typeof ConflictChoice>;

export const EnvironmentKind = z.enum(["LOCAL", "DEVELOPMENT", "STAGING", "PRODUCTION", "CUSTOM"]);
export type EnvironmentKind = z.infer<typeof EnvironmentKind>;

/** Režim autonomie pro danou akci (spec §57 vzor AUTO/APPROVAL/DENIED). */
export const PolicyMode = z.enum(["AUTO", "APPROVAL", "DENIED"]);
export type PolicyMode = z.infer<typeof PolicyMode>;

/**
 * Akce řízené autonomy policies (spec §15/§31/§87). Vynucování přibývá
 * po fázích — Fáze 2 ukládá konfiguraci, aby onboarding i Settings
 * odpovídaly designu a další fáze měly co číst.
 */
export const PolicyAction = z.enum([
  "TASK_FROM_SCOPE", // AI smí vytvářet tasky ze schváleného scope → READY
  "PROPOSAL_AUTO_APPROVE", // novel AI návrh bez lidského schválení
  "KNOWLEDGE_AUTO_UPDATE", // AI smí sama aktualizovat Knowledge
  "BRANCH_CREATE",
  "PR_CREATE",
  "PR_MERGE",
  "SUPPORT_SEND", // odeslání odpovědi zákazníkovi (ne-dev příklad)
]);
// Deploy tu schválně není: režim nasazení nese `Environment.deployPolicy`,
// protože je vázaný na konkrétní prostředí i jeho git větev. Dokud tu byly
// i akce DEPLOY_STAGING/DEPLOY_PRODUCTION, nastavovala se táž věc dvakrát
// a nikdo je nevynucoval — worker se řídí prostředím (WORKER_CONNECTOR §deploy).
export type PolicyAction = z.infer<typeof PolicyAction>;

/**
 * Výchozí bezpečné nastavení policies nového projektu.
 * Git konvence: vývoj jde přímo do staging větve (`beta`), bez pull
 * requestů; produkci nasazuje owner ručně mergem `beta` → `main`.
 */
export const DEFAULT_PROJECT_POLICIES: Record<PolicyAction, PolicyMode> = {
  TASK_FROM_SCOPE: "AUTO",
  PROPOSAL_AUTO_APPROVE: "DENIED",
  KNOWLEDGE_AUTO_UPDATE: "APPROVAL",
  BRANCH_CREATE: "AUTO", // založení staging větve (beta), když chybí
  PR_CREATE: "DENIED", // PR flow se nepoužívá — push přímo do beta
  PR_MERGE: "DENIED", // beta → main merguje owner ručně
  SUPPORT_SEND: "APPROVAL",
};

export type ThemePreference = "DARK" | "LIGHT" | "SYSTEM";

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  disabled: boolean;
  createdAt: string;
  /** Volba motivu účtu; výchozí je tmavý. */
  themePreference: ThemePreference;
}

export interface ProjectMemberDTO {
  userId: string;
  role: ProjectMemberRole;
  user: Pick<UserDTO, "id" | "email" | "name" | "role">;
}

export interface ProjectDTO {
  id: string;
  slug: string;
  name: string;
  description: string;
  kind: ProjectKind;
  lifecycle: ProjectLifecycle;
  businessPriority: ProjectPriority;
  attentionScore: number;
  health: number | null;
  /** Krátký stavový řádek pod názvem (co se právě děje) — zatím kurátorský/AI text. */
  statusLine: string | null;
  /** Právě běžící task (IN_PROGRESS/TESTING) — plněno jen v seznamu projektů. */
  runningTask: ProjectRunningTaskDTO | null;
  /** Projekt je z pohledu Factory připravený k produkci (kaskáda vývoj → testy → ready). */
  factoryReadyAt: string | null;
  /**
   * Postup plánu vývoje a odhad dokončení — sloupec FACTORY-READY na Command
   * Centeru (p9a). `etaDays` je null, když se nedá odhadnout (nulové tempo,
   * prázdný plán). Plněno jen v seznamu projektů.
   */
  planDone: number;
  planTotal: number;
  etaDays: number | null;
  /** Stav šesti systémových kol — sloupec KOLA 1–6 na Command Centeru (p9a). */
  rounds: { key: string; order: number; state: "done" | "running" | "waiting" | "blocked" }[];
  createdAt: string;
  updatedAt: string;
}

/** Jedno ze šesti systémových kol Factory (p2a „Automatika"). */
export interface AutomationRoundDTO {
  /** Pořadí v kaskádě, 1–6. */
  order: number;
  key: string;
  title: string;
  /** `done` splněno · `running` běží · `waiting` čeká na spouštěč · `blocked` drží brána. */
  state: "done" | "running" | "waiting" | "blocked";
  /** Jednou větou, co se v kole stalo nebo na co čeká. */
  note: string;
  taskNumber: number | null;
}

/** Brána, která projekt drží (nebo je splněná). */
export interface AutomationGateDTO {
  key: string;
  label: string;
  ok: boolean;
  /** Vysvětlení u nesplněné brány — co konkrétně stojí. */
  detail: string | null;
}

export interface AutomationStateDTO {
  rounds: AutomationRoundDTO[];
  gates: AutomationGateDTO[];
  /** Kvalita produktu s rozpadem — stat strip i Overview (PROTOTYP §7.4). */
  health: ProjectHealthDTO;
  /** Podmínky factory-ready: odškrtaný plán, žádná otevřená práce, čisté testovací kolo. */
  readiness: {
    planDone: number;
    planTotal: number;
    openWork: number;
    inQa: number;
    lastTestFindings: number | null;
    factoryReadyAt: string | null;
  };
  /** Právě běžící tasky projektu. */
  running: { number: number; title: string; epScore: number; system: boolean; progress: number | null; workerName: string | null }[];
  /** Tasky, které něco drží. */
  blocked: { number: number; title: string; reason: string }[];
}

export interface ProjectRunningTaskDTO {
  number: number;
  title: string;
  /** Kolik tasků v projektu právě běží celkem. */
  totalRunning: number;
}

export interface ProjectDetailDTO extends ProjectDTO {
  members: ProjectMemberDTO[];
}

export interface AuditEventDTO {
  id: string;
  ts: string;
  actorType: ActorType;
  actorId: string | null;
  actorLabel: string;
  action: string;
  projectId: string | null;
  projectName: string | null;
  entityType: string | null;
  entityId: string | null;
  summary: string;
  reason: string | null;
  link: string | null;
  before: unknown;
  after: unknown;
}

export interface SessionInfoDTO {
  user: UserDTO;
  memberships: { projectId: string; role: ProjectMemberRole }[];
}

// ---------------------------------------------------------------------------
// Fáze 2 — DTO
// ---------------------------------------------------------------------------

export interface UploadDTO {
  id: string;
  projectId: string;
  applicationId: string | null;
  kind: UploadKind;
  status: UploadStatus;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  note: string | null;
  /** Tasky založené workerem při zpracování podkladu (delegací). */
  taskRefs: FeedbackTaskRef[];
  uploadedByLabel: string | null;
  createdAt: string;
}

export interface DesignVersionDTO {
  id: string;
  applicationId: string;
  uploadId: string;
  version: number;
  label: string | null;
  isCurrent: boolean;
  filename: string;
  sizeBytes: number;
  createdAt: string;
}

export interface ApplicationDTO {
  id: string;
  projectId: string;
  slug: string;
  name: string;
  description: string;
  codebases: { id: string; slug: string; name: string }[];
  designVersions: DesignVersionDTO[];
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeDocMetaDTO {
  id: string;
  projectId: string;
  folder: string;
  slug: string;
  title: string;
  status: KnowledgeDocStatus;
  revisionCount: number;
  hasOpenConflict: boolean;
  /// Kdy si dokument načíst — spouštěč do rozcestníku workera.
  readWhen: string;
  /// Prázdný spouštěč = dokument se do vaultu workera vůbec nedostane.
  indexed: boolean;
  updatedAt: string;
}

export interface KnowledgeRevisionDTO {
  id: string;
  rev: number;
  title: string;
  changeSummary: string;
  sourceType: KnowledgeSourceType;
  sourceUploadId: string | null;
  sourceUploadName: string | null;
  sourceRef: string | null;
  actorType: ActorType;
  actorLabel: string;
  createdAt: string;
}

export interface KnowledgeDocDTO extends KnowledgeDocMetaDTO {
  content: string;
  supersededByDocId: string | null;
  revisions: KnowledgeRevisionDTO[];
}

export interface KnowledgeTreeDTO {
  /** Sloučení nakonfigurovaných složek projektu a složek s dokumenty. */
  folders: { folder: string; docs: KnowledgeDocMetaDTO[] }[];
}

export interface ConflictOptionDTO {
  label: string;
  detail: string | null;
  sourceRef: string | null;
}

export interface KnowledgeConflictDTO {
  id: string;
  projectId: string;
  docId: string | null;
  docTitle: string | null;
  title: string;
  description: string;
  optionA: ConflictOptionDTO;
  optionB: ConflictOptionDTO;
  recommended: "A" | "B" | null;
  status: ConflictStatus;
  chosen: ConflictChoice | null;
  resolutionNote: string | null;
  resolvedByLabel: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface CodebaseDependencyDTO {
  id: string;
  fromId: string;
  toId: string;
  fromSlug: string;
  toSlug: string;
  label: string | null;
}

export interface CodebaseDTO {
  id: string;
  projectId: string;
  slug: string;
  name: string;
  description: string;
  repoUrl: string | null;
  defaultBranch: string;
  devBranch: string | null;
  branchPolicy: string | null;
  prPolicy: string | null;
  testPolicy: string | null;
  techStack: string[];
  dodChecks: DodCheck[];
  applications: { id: string; slug: string; name: string }[];
  environments: { id: string; key: string; name: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface EnvironmentDTO {
  id: string;
  projectId: string;
  key: string;
  name: string;
  kind: EnvironmentKind;
  url: string | null;
  deployPolicy: PolicyMode;
  /**
   * Git větev environmentu (staging=beta, production=main); deploy = dostat
   * kód do ní. Worker pushuje vývoj přímo do staging větve (založí ji,
   * když chybí); do produkční merguje jen owner.
   */
  branch: string | null;
  order: number;
  /** Kde environment běží; ZEABUR = ještě nepřestěhováno (Zeabur skončil), null = nic nestojí. */
  hostingProvider: HostingProvider | null;
  /** Projekt u hostingu (Railway project ID / Coolify project uuid) — nahlásil worker; null = infrastruktura ještě nestojí. */
  hostingProjectId: string | null;
}

/** Hosting aplikací projektů — nová infrastruktura COOLIFY, RAILWAY dobíhá, ZEABUR je historie. */
export type HostingProvider = "ZEABUR" | "RAILWAY" | "COOLIFY";

/** Build/deploy z hostingu (zrcadlo pro Releases UI — „co je kde nasazené"). */
export interface DeploymentDTO {
  id: string;
  environmentId: string;
  codebaseSlug: string | null;
  /** Veřejná URL service (mapping CodebaseEnvironment) — „otevřít aplikaci". */
  url: string | null;
  provider: HostingProvider;
  /** ID deploye u hostingu (Railway deployment UUID / Coolify deployment uuid). */
  hostingDeploymentId: string;
  /** Odkaz na deploy v UI hostingu (Coolify); null = nevíme kam. */
  hostingUrl: string | null;
  /** Stav z hostingu (Railway: BUILDING/DEPLOYING/SUCCESS/FAILED/CRASHED/REMOVED…, Coolify mapovaný na totéž + CANCELLED) — neuzavřený výčet. */
  status: string;
  commitSha: string | null;
  commitMessage: string | null;
  branch: string | null;
  deployedAt: string | null;
  finishedAt: string | null;
}

/** Stav nasazení jednoho environmentu: poslední deploy per service + historie. */
export interface EnvironmentDeploymentsDTO {
  environment: EnvironmentDTO;
  /** Poslední deployment každé service (co právě běží / naposledy proběhlo). */
  current: DeploymentDTO[];
  /** Posledních pár buildů napříč services, nejnovější první. */
  history: DeploymentDTO[];
}

export interface ProjectPolicyDTO {
  action: PolicyAction;
  mode: PolicyMode;
}

// ---------------------------------------------------------------------------
// Fáze 3 — Organizace & práce
// ---------------------------------------------------------------------------

export const TaskPriority = z.enum(["P0", "P1", "P2", "P3"]);
export type TaskPriority = z.infer<typeof TaskPriority>;

export const TaskOrigin = z.enum(["SCOPE", "PROPOSAL", "INCIDENT", "ROUTINE", "DISCOVERY"]);
export type TaskOrigin = z.infer<typeof TaskOrigin>;

export const DecisionKind = z.enum(["QUESTION", "PROPOSAL", "APPROVAL", "ALERT"]);
export type DecisionKind = z.infer<typeof DecisionKind>;

export const DecisionUrgency = z.enum(["URGENT", "HIGH", "NORMAL", "LOW"]);
export type DecisionUrgency = z.infer<typeof DecisionUrgency>;

export const DecisionStatus = z.enum(["OPEN", "RESOLVED", "DISMISSED"]);
export type DecisionStatus = z.infer<typeof DecisionStatus>;

export const ProposalStatus = z.enum(["PENDING", "APPROVED", "REJECTED"]);
export type ProposalStatus = z.infer<typeof ProposalStatus>;

export const GoalScope = z.enum(["COMPANY", "PROJECT"]);
export type GoalScope = z.infer<typeof GoalScope>;

export const GoalHealth = z.enum(["ON_TRACK", "AT_RISK", "OFF_TRACK"]);
export type GoalHealth = z.infer<typeof GoalHealth>;

export const GoalStatus = z.enum(["ACTIVE", "DONE", "ARCHIVED"]);
export type GoalStatus = z.infer<typeof GoalStatus>;

export const MilestoneStatus = z.enum(["OPEN", "DONE"]);
export type MilestoneStatus = z.infer<typeof MilestoneStatus>;

export const ReleaseStatus = z.enum(["DRAFT", "IN_PROGRESS", "RELEASED"]);
export type ReleaseStatus = z.infer<typeof ReleaseStatus>;

export const WorkSessionStatus = z.enum(["ACTIVE", "COMPLETED", "PAUSED", "FAILED", "INTERRUPTED"]);
export type WorkSessionStatus = z.infer<typeof WorkSessionStatus>;

// ---------------------------------------------------------------------------
// Fáze 5 — QA & Development Automation
// ---------------------------------------------------------------------------

export const QaVerdict = z.enum(["PASSED", "CHANGES_REQUESTED"]);
export type QaVerdict = z.infer<typeof QaVerdict>;

export const TestStatus = z.enum(["PASSED", "FAILED", "SKIPPED"]);
export type TestStatus = z.infer<typeof TestStatus>;

/** Definition of Done check (spec §53) — příkaz je volitelný (discoverable). */
export const DodCheck = z.object({
  key: z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/),
  label: z.string().min(1).max(120),
  command: z.string().max(300).optional(),
});
export type DodCheck = z.infer<typeof DodCheck>;

/**
 * Pitch Perfect check — je v DoD každého tasku: PASSED vyžaduje nahrané
 * screenshoty (server vynucuje), task bez UI změn reportuje SKIPPED.
 */
export const SCREENS_DOD_CHECK: DodCheck = {
  key: "screens",
  label: "Pitch Perfect screenshoty — desktop/mobil/tablet (u UI práce)",
};

/** Výchozí DoD, když codebase nemá vlastní (spec §53). */
export const DEFAULT_DOD_CHECKS: DodCheck[] = [
  { key: "unit", label: "unit testy" },
  { key: "lint", label: "lint + typecheck" },
  { key: "build", label: "build" },
  { key: "acceptance", label: "acceptance criteria ověřena" },
  SCREENS_DOD_CHECK,
];

/** DoD tasku vždy obsahuje screens check — i když má codebase vlastní sadu. */
export function ensureScreensCheck(checks: DodCheck[]): DodCheck[] {
  return checks.some((c) => c.key === SCREENS_DOD_CHECK.key) ? checks : [...checks, SCREENS_DOD_CHECK];
}

export const SessionArtifactKind = z.enum(["BRANCH", "COMMIT", "PR", "TEST", "FILE", "OTHER"]);
export type SessionArtifactKind = z.infer<typeof SessionArtifactKind>;

export const SessionLogKind = z.enum([
  "START",
  "PLAN",
  "CHECKPOINT",
  "ARTIFACT",
  "DISCOVERY",
  "BLOCKER",
  "NOTE",
  "FINISH",
]);
export type SessionLogKind = z.infer<typeof SessionLogKind>;

/** Strukturovaný handoff (spec §50) — minimum pro pokračování jiné session. */
export const SessionHandoff = z.object({
  completed: z.array(z.string().max(300)).max(30).default([]),
  state: z.string().max(2000).default(""),
  remaining: z.array(z.string().max(300)).max(30).default([]),
  discoveries: z.array(z.string().max(300)).max(20).default([]),
  blockers: z.array(z.string().max(300)).max(20).default([]),
  // null posílají workeři, když větev nevznikla — nesmí to shodit celý finish
  branch: z.string().max(200).nullish().transform((v) => v ?? undefined),
  testsRun: z.array(z.string().max(200)).max(20).default([]),
  nextAction: z.string().max(500).default(""),
});
export type SessionHandoff = z.infer<typeof SessionHandoff>;

/** Druh stavu ve workflow — řídí vykreslení (uzel/gate/handoff) a chování. */
export const WorkflowStateKind = z.enum(["START", "WORK", "GATE", "BLOCKED", "DONE", "FAILED"]);
export type WorkflowStateKind = z.infer<typeof WorkflowStateKind>;

export const WorkflowState = z.object({
  key: z.string().regex(/^[A-Z][A-Z0-9_]{1,30}$/),
  label: z.string().min(1).max(40),
  tone: z.enum(["cyan", "green", "amber", "red", "dim", "violet"]).default("cyan"),
  kind: WorkflowStateKind.default("WORK"),
  order: z.number().int(),
  /** Poznámka pod uzlem (auto pravidla apod.). */
  note: z.string().max(120).optional(),
});
export type WorkflowState = z.infer<typeof WorkflowState>;

export const WorkflowTransition = z.object({
  from: z.string(),
  to: z.string(),
  label: z.string().max(60).optional(),
  kind: z.enum(["NORMAL", "RETURN", "HANDOFF"]).default("NORMAL"),
});
export type WorkflowTransition = z.infer<typeof WorkflowTransition>;

export const WorkflowConfig = z.object({
  states: z.array(WorkflowState).min(2),
  transitions: z.array(WorkflowTransition),
});
export type WorkflowConfig = z.infer<typeof WorkflowConfig>;

export const AcceptanceCriterion = z.object({
  id: z.string(),
  text: z.string().min(1).max(500),
  met: z.boolean().default(false),
});
export type AcceptanceCriterion = z.infer<typeof AcceptanceCriterion>;

export const PlanStep = z.object({
  text: z.string().min(1).max(300),
  state: z.enum(["TODO", "DOING", "DONE"]).default("TODO"),
});
export type PlanStep = z.infer<typeof PlanStep>;

export const DecisionAlternative = z.object({
  key: z.string().min(1).max(40),
  label: z.string().min(1).max(200),
  impact: z.string().max(500).default(""),
});
export type DecisionAlternative = z.infer<typeof DecisionAlternative>;

/**
 * Rozpad health projektu — proč skóre není sto. Hodnota je záporná (odečet),
 * stejně jako u EP je vidět, co konkrétně číslo dělá.
 */
export interface HealthFactorDTO {
  key: string;
  label: string;
  value: number;
}

export interface ProjectHealthDTO {
  /** `null` u projektu, ze kterého se ještě nedá kvalita odvodit. */
  score: number | null;
  factors: HealthFactorDTO[];
}

/** Rozpad Effective Priority — vysvětlitelnost v UI (spec §34). */
export interface EpFactorDTO {
  key: string;
  label: string;
  value: number;
}

export const TemplateEnvironmentConfig = z.object({
  key: z.string(),
  name: z.string(),
  kind: EnvironmentKind,
  deployPolicy: PolicyMode,
  branch: z.string().nullable().optional(),
});
export type TemplateEnvironmentConfig = z.infer<typeof TemplateEnvironmentConfig>;

/** Konfigurace šablony projektu — validuje se i při čtení z DB (JSONB). */
export const ProjectTemplateConfig = z.object({
  kind: ProjectKind,
  environments: z.array(TemplateEnvironmentConfig),
  knowledgeFolders: z.array(z.string()),
  policies: z.partialRecord(PolicyAction, PolicyMode),
});
export type ProjectTemplateConfig = z.infer<typeof ProjectTemplateConfig>;

export interface ProjectTemplateDTO {
  key: string;
  name: string;
  description: string;
  builtIn: boolean;
  config: ProjectTemplateConfig;
}

// ---------------------------------------------------------------------------
// Fáze 3 — DTO
// ---------------------------------------------------------------------------

export interface WorkflowDTO {
  key: string;
  name: string;
  builtIn: boolean;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
}

/**
 * Aktuální Claude modely nabízené v UI jako preferovaný model (modelHint).
 * Workeři jsou Claude sessions — hint musí být model, který Claude API podporuje.
 */
export const CLAUDE_MODEL_IDS = [
  "claude-fable-5",
  "claude-opus-5-5",
  "claude-opus-5",
  "claude-sonnet-5",
  "claude-sonnet-4-6",
  "claude-haiku-4-5",
] as const;
export type ClaudeModelId = (typeof CLAUDE_MODEL_IDS)[number];

export interface DepartmentDTO {
  id: string;
  key: string;
  name: string;
  color: string;
  description: string;
  purpose: string;
  basePrompt: string;
  /** Preferovaný model pro tasky oddělení (např. claude-opus-5-5); null = nechá se na workeru. */
  modelHint: string | null;
  capabilities: string[];
  toolsNote: string;
  autonomyNote: string;
  workflowKey: string;
  enabled: boolean;
  builtIn: boolean;
  /** Počty pro kartu registru. */
  projectCount: number;
  openTaskCount: number;
}

export interface TaskRefDTO {
  id: string;
  number: number;
  title: string;
  status: string;
  projectId: string;
  projectSlug?: string;
}

export interface TaskDTO {
  id: string;
  projectId: string;
  projectSlug: string;
  projectName: string;
  number: number;
  title: string;
  description: string;
  status: string;
  priority: TaskPriority;
  epScore: number;
  epBreakdown: EpFactorDTO[];
  pinnedAt: string | null;
  deadline: string | null;
  departmentId: string | null;
  departmentKey: string | null;
  departmentName: string | null;
  departmentColor: string | null;
  parentId: string | null;
  milestoneId: string | null;
  initiativeId: string | null;
  releaseId: string | null;
  acceptance: AcceptanceCriterion[];
  plan: PlanStep[];
  progress: number | null;
  origin: TaskOrigin;
  blockedByDecisionId: string | null;
  qaVerdict: QaVerdict | null;
  reworkCount: number;
  /** Nesplněné závislosti (blokující tasky). */
  openDependencyCount: number;
  applications: { id: string; slug: string }[];
  codebases: { id: string; slug: string }[];
  createdByType: ActorType;
  createdByLabel: string;
  createdAt: string;
  updatedAt: string;
}

/** Obrazová příloha tasku (screenshot nálezu) — nahrává worker. */
export interface TaskAttachmentDTO {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  note: string | null;
  createdByLabel: string;
  createdAt: string;
}

export interface TaskDetailDTO extends TaskDTO {
  parent: TaskRefDTO | null;
  subtasks: TaskRefDTO[];
  dependsOn: (TaskRefDTO & { done: boolean })[];
  dependedBy: TaskRefDTO[];
  discussion: TaskDiscussionDTO[];
  attachments: TaskAttachmentDTO[];
  milestoneTitle: string | null;
  initiativeTitle: string | null;
  goalTitle: string | null;
  releaseVersion: string | null;
  /** Fáze 5 — QA & traceability. */
  qaVerdict: QaVerdict | null;
  qaNote: string | null;
  qaByLabel: string | null;
  qaAt: string | null;
  reworkCount: number;
  /** DoD checks: sjednocení checků napojených codebases, jinak výchozí sada. */
  dodChecks: DodCheck[];
  testRuns: TestRunDTO[];
  /** Git a další artefakty napříč všemi sessions tasku (spec §54). */
  gitArtifacts: (SessionArtifactDTO & { sessionId: string })[];
  originProposalTitle: string | null;
  /** Pitch Perfect obrazovky, jejichž screenshoty vznikly v sessions tasku. */
  screens: TaskScreenRefDTO[];
}

/** Reference na Pitch Perfect obrazovku z detailu tasku. */
export interface TaskScreenRefDTO {
  id: string;
  key: string;
  title: string;
  status: UiScreenStatus;
}

export interface TaskDiscussionDTO {
  id: string;
  actorType: ActorType;
  actorLabel: string;
  text: string;
  createdAt: string;
}

export interface DecisionDTO {
  id: string;
  projectId: string | null;
  projectSlug: string | null;
  projectName: string | null;
  kind: DecisionKind;
  title: string;
  context: string;
  recommendation: string;
  reasoning: string;
  alternatives: DecisionAlternative[];
  impact: string;
  urgency: DecisionUrgency;
  status: DecisionStatus;
  /** Překážka zastavila víc tasků → dokud je OPEN, projekt se neclaimuje. */
  blocksProject: boolean;
  resolution: string | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  proposalId: string | null;
  blockedTasks: TaskRefDTO[];
  createdByType: ActorType;
  createdByLabel: string;
  createdAt: string;
}

export type HumanTaskStatus = "OPEN" | "DONE" | "DISMISSED";

/**
 * Úkol pro člověka — druhá ze čtyř věcí, které se dřív cpaly do jedné
 * schránky. Nemá varianty a nedá se schválit; zavírá se NÁLEZEM, který jde
 * workerovi jako vstup.
 */
export interface HumanTaskDTO {
  id: string;
  projectId: string | null;
  projectSlug: string | null;
  projectName: string | null;
  title: string;
  context: string;
  /** Proč to nemůže udělat worker. */
  whyHuman: string;
  /** Jaký nález se čeká — ať owner ví, co má napsat. */
  expectedFinding: string;
  urgency: DecisionUrgency;
  status: HumanTaskStatus;
  blocksProject: boolean;
  /** Co člověk zjistil. Tohle dostane worker, ne jen „hotovo". */
  finding: string | null;
  resolvedAt: string | null;
  resolvedByLabel: string | null;
  blockedTasks: TaskRefDTO[];
  createdByType: ActorType;
  createdByLabel: string;
  createdAt: string;
}

/** Sloučený inbox: rozhodnutí + otevřené knowledge konflikty (spec §32). */
export interface InboxItemDTO {
  itemType: "DECISION" | "CONFLICT";
  id: string;
  title: string;
  urgency: DecisionUrgency;
  projectId: string | null;
  projectSlug: string | null;
  projectName: string | null;
  blocksSummary: string;
  createdAt: string;
  decision?: DecisionDTO;
  conflict?: KnowledgeConflictDTO;
}

export interface ProposalDTO {
  id: string;
  projectId: string;
  projectSlug: string | null;
  projectName: string | null;
  title: string;
  description: string;
  rationale: string;
  status: ProposalStatus;
  urgency: DecisionUrgency;
  proposedByLabel: string;
  approvedTaskId: string | null;
  /** Číslo tasku, který ze schválení vznikl — kvůli odkazu z obrazovky. */
  approvedTaskNumber: number | null;
  /**
   * Historické rozhodnutí, které si návrh vyrobil, aby se dostal před ownera.
   * Nové návrhy ho nemají; u starých se s vyřízením návrhu zavře i ono.
   */
  decisionId: string | null;
  decidedByLabel: string | null;
  decidedNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface InitiativeDTO {
  id: string;
  goalId: string;
  title: string;
  note: string;
  status: string;
  taskCount: number;
  doneTaskCount: number;
}

export interface GoalDTO {
  id: string;
  scope: GoalScope;
  projectId: string | null;
  projectSlug: string | null;
  projectName: string | null;
  parentGoalId: string | null;
  title: string;
  description: string;
  metric: string;
  targetValue: number | null;
  currentValue: number | null;
  unit: string;
  deadline: string | null;
  health: GoalHealth;
  progress: number;
  ownerLabel: string;
  status: GoalStatus;
  initiatives: InitiativeDTO[];
}

export interface MilestoneDTO {
  id: string;
  projectId: string;
  title: string;
  note: string;
  deadline: string | null;
  status: MilestoneStatus;
  taskCount: number;
  doneTaskCount: number;
  /** 0–100 z tasků. */
  progress: number;
  /** Deadline risk heuristika (spec §25). */
  atRisk: boolean;
}

export interface ReleaseDTO {
  id: string;
  projectId: string;
  version: string;
  name: string;
  notes: string;
  status: ReleaseStatus;
  releasedAt: string | null;
  taskCount: number;
  doneTaskCount: number;
  /** Obsah release s QA stavem (traceability, spec §55). */
  tasks: (TaskRefDTO & { qaVerdict: QaVerdict | null })[];
}

// ---------------------------------------------------------------------------
// Fáze 4 — DTO
// ---------------------------------------------------------------------------

export interface WorkerDTO {
  id: string;
  name: string;
  capabilities: string[];
  departmentIds: string[];
  departmentNames: string[];
  /// Prázdné = všechny projekty (viz CreateWorkerInput.projectIds).
  projectIds: string[];
  projectNames: string[];
  enabled: boolean;
  online: boolean;
  lastSeenAt: string | null;
  /// Limity předplatného stroje (co ukazuje `/usage`); null = stroj je zatím
  /// nehlásil, typicky starší verze runneru.
  rateLimit: WorkerRateLimitDTO | null;
  /// Co na stroji běží: verze runneru, platforma, verze Claude Code CLI.
  /// null = stroj to zatím nehlásil (runner starší než 1.0.14).
  runner: WorkerRunnerDTO | null;
  activeSession: WorkSessionSummaryDTO | null;
  /// Předplatné stroje — účet, tarif a měsíční cena (§7.2). Bez ceny se
  /// spotřeba ukazuje jen v procentech týdenního limitu.
  billing: WorkerBillingDTO | null;
  createdAt: string;
}

/**
 * Provozní historie stroje — kdy běžel, kdy stál a co dovolí tarif.
 * Hodinová osa (`hourly`) drží minuty práce v každé hodině okna; nula je
 * prostoj, ne chybějící údaj.
 */
export interface WorkerActivityDTO {
  workerId: string;
  name: string;
  enabled: boolean;
  tariff: string | null;
  account: string | null;
  monthlyPrice: number | null;
  currency: string;
  /** Minuty práce po hodinách, 0–60. Délka = `WorkersActivityDTO.hourlyCount`. */
  hourly: number[];
  totals: {
    sessions: number;
    completed: number;
    interrupted: number;
    paused: number;
    failed: number;
    workedMinutes: number;
    /** Délka okna — poslední hodina je rozdělaná, proto to není násobek 60. */
    windowMinutes: number;
    utilizationPct: number;
    turns: number;
    tasksDone: number;
    avgSessionMinutes: number;
    /** Pauzy delší než 30 minut — podpis prázdné fronty. */
    idleGaps: number;
    longestIdleMinutes: number;
  };
  /**
   * Co dovolí tarif. `null`, když se nepodařilo ohraničit týdenní okno —
   * bez něj by čísla míchala spotřebu z několika oken dohromady.
   */
  capacity: {
    weekWindowStart: string;
    weekWindowEnd: string;
    weekPctUsed: number;
    /** Kolik % týdenního limitu spolkne hodina práce — z toho plyne strop. */
    weekPctPerWorkedHour: number | null;
    /** Spotřeba na jednotku práce; jediné číslo srovnatelné napříč stroji. */
    weekPctPer1000Turns: number | null;
    sessionPctPer1000Turns: number | null;
    /** Kolik hodin práce tarif uveze za týden. */
    ceilingHoursPerWeek: number | null;
    /** Týž strop jako obsazenost stroje (168 h = 100 %). */
    ceilingUtilizationPct: number | null;
    /** Kdy stroj při současném tempu dojede na strop; `null` = už je na něm. */
    projectedExhaustionAt: string | null;
  } | null;
}

export interface WorkersActivityDTO {
  windowDays: number;
  /** Začátek hodinové osy. */
  hourlyStartAt: string;
  hourlyCount: number;
  generatedAt: string;
  workers: WorkerActivityDTO[];
}

/** Ekonomika provozu — měrnou jednotkou je % týdenního limitu (PROTOTYP §7.2). */
export interface EconomyDTO {
  windowDays: number;
  /** Součet spotřeby sessions v okně, v % týdenního limitu. */
  totalPct: number;
  /** Přepočet na měnu; `null`, když žádný worker nemá vyplněnou cenu tarifu. */
  totalPrice: number | null;
  workers: {
    id: string;
    name: string;
    account: string | null;
    tariff: string | null;
    monthlyPrice: number | null;
    currency: string;
    /** Vyčerpaná procenta týdenního limitu podle `/usage`. */
    weekPct: number | null;
    resetsAt: string | null;
    reportedAt: string | null;
  }[];
  projects: { projectId: string; name: string; pct: number; price: number | null }[];
  tasks: {
    number: number;
    title: string;
    projectName: string;
    pct: number;
    price: number | null;
    sessions: number;
    rework: number;
  }[];
  /** Účty, na kterých běží víc workerů — jejich čísla jsou nadsazená. */
  sharedAccounts: string[];
}

export interface WorkerBillingDTO {
  account: string | null;
  tariff: string | null;
  monthlyPrice: number | null;
  currency: string;
  /**
   * Cena jednoho procenta týdenního limitu = měsíční cena / 4,33 týdne / 100.
   * `null`, když cena chybí.
   */
  pricePerPercent: number | null;
}

/**
 * Poznámka Factory brainu — poučení z provozu. `indexed=false` znamená
 * kandidáta bez spouštěče: workerovi se nikdy nenabídne, dokud mu ho někdo
 * nedoplní.
 */
export interface BrainNoteDTO {
  id: string;
  slug: string;
  title: string;
  /// Kdy si poznámku načíst — spouštěč vyhledání.
  readWhen: string;
  body: string;
  scope: "FACTORY" | "PROJECT";
  /// Kolikrát se problém stal. Vysoké číslo = VRACÍ se, ne že je poučení jistější.
  occurrences: number;
  lastSeenAt: string;
  needsReview: boolean;
  archived: boolean;
  linkSlugs: string[];
  sourceRef: string | null;
  createdByLabel: string;
  createdAt: string;
  updatedAt: string;
  project: { id: string; slug: string; name: string } | null;
  indexed: boolean;
}

/**
 * Brain jednoho projektu: mapa kódu + poučení platná jen tady. Dvě půlky,
 * které ve Factory leží v jiných tabulkách — člověk se ale ptá na obojí naráz.
 */
export interface ProjectBrainDTO {
  lessons: BrainNoteDTO[];
  maps: {
    id: string;
    folder: string;
    slug: string;
    title: string;
    /// Kdy si mapu načíst; prázdné = workerovi se nikdy nenabídne.
    readWhen: string;
    codebaseSlug: string | null;
    /// Commit, proti kterému byla mapa ověřená. Null = neví se, jak je stará.
    codeCommit: string | null;
    codeVerifiedAt: string | null;
    updatedAt: string;
    chars: number;
    indexed: boolean;
  }[];
  /// Codebases bez mapy — na ty sweeper zakládá kolo „Zmapovat codebase".
  unmapped: { slug: string; name: string }[];
  /**
   * Kolik projektových dokumentů se do vaultu workera opravdu vejde. Vault má
   * strop; co se přes něj nevejde, worker nikdy neuvidí — a bez tohohle čísla
   * to nebylo poznat nikde.
   */
  vault: {
    /// Dokumenty se spouštěčem, tedy kandidáti na vault.
    eligible: number;
    /// Strop vaultu (`MAX_PROJECT_DOCS`).
    limit: number;
    /// Kolik jich strop uřízl. > 0 = část brainu je workerovi neviditelná.
    omitted: number;
  };
}

export interface BrainGraphDTO {
  nodes: {
    slug: string;
    title: string;
    scope: "FACTORY" | "PROJECT";
    /// Poučení z provozu vs. mapa kódu — v grafu se liší barvou.
    kind: "lesson" | "map";
    occurrences: number;
    /// Bez spouštěče se do rozcestníku nedostane.
    indexed: boolean;
    /// Odkazuje na ni aspoň jedna jiná poznámka.
    linked: boolean;
  }[];
  edges: { from: string; to: string; unresolved: boolean }[];
  /// Cíle odkazů, které zatím neexistují — říkají, co v brainu chybí.
  missing: string[];
}

export interface WorkerRunnerDTO {
  /// Verze desktopové aplikace workera, např. „1.0.14".
  version: string | null;
  /// např. „darwin/arm64", „win32/x64".
  platform: string | null;
  /// Verze Claude Code CLI, např. „2.1.234". Rozdíl mezi stroji vysvětluje,
  /// proč se jeden chová jinak — starší CLI nezná novější přepínače.
  claudeVersion: string | null;
}

export interface WorkerRateLimitDTO {
  /// allowed | allowed_warning | rejected; null, než dorazí první běh
  status: string | null;
  /// five_hour | seven_day | seven_day_opus | seven_day_overage_included
  type: string | null;
  resetsAt: string | null;
  overage: boolean;
  /// VYČERPANÁ procenta (ne zbývající) z `claude -p "/usage"`.
  sessionPct: number | null;
  weekPct: number | null;
  weekModelPct: number | null;
  /// Jméno modelu u týdenního model-specifického limitu (např. „Opus").
  weekModelName: string | null;
  /// Kdy to runner naposledy hlásil — po pár hodinách je údaj bezcenný.
  reportedAt: string;
}

export interface WorkSessionSummaryDTO {
  id: string;
  workerId: string;
  workerName: string;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  projectId: string;
  projectSlug: string;
  projectName: string;
  departmentName: string | null;
  status: WorkSessionStatus;
  startedAt: string;
  endedAt: string | null;
  leaseExpiresAt: string;
  models: string[];
  tokensTotal: number | null;
  /// Kolik procent týdenního limitu session ukrojila (rozdíl `/usage` před
  /// a po). Srozumitelnější měřítko než tokeny — limit je to, co dochází.
  weekPctSpent: number | null;
  /// Kolik kroků (požadavků na model) session spotřebovala, a jaký kontext
  /// v průměru v každém z nich četla. Vysoký `turns` při malém
  /// `avgContextTokens` = hodně drobných kroků; opačně = session táhne
  /// zbytečně velký kontext. Null u sessions ze starších runnerů.
  turns: number | null;
  avgContextTokens: number | null;
  progress: number;
  currentActivity: string;
}

export interface SessionLogDTO {
  id: string;
  kind: SessionLogKind;
  text: string;
  createdAt: string;
  /** Vyřešení blokace (jen kind BLOCKER) — null = stále otevřená. */
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNote: string | null;
}

export interface SessionArtifactDTO {
  id: string;
  kind: SessionArtifactKind;
  label: string;
  ref: string | null;
  createdAt: string;
}

export interface WorkSessionDetailDTO extends WorkSessionSummaryDTO {
  tokensInput: number | null;
  tokensOutput: number | null;
  resultSummary: string;
  handoff: SessionHandoff | null;
  logs: SessionLogDTO[];
  artifacts: SessionArtifactDTO[];
  plan: PlanStep[];
  /** Předchozí sessions stejného tasku (session chain, spec §45). */
  chain: { id: string; workerName: string; status: WorkSessionStatus; startedAt: string; endedAt: string | null }[];
}

/** Blokace nahlášená workerem (log kind BLOCKER) — centrální čtení napříč sessions. */
export interface WorkerBlockerDTO {
  id: string;
  text: string;
  createdAt: string;
  sessionId: string;
  sessionStatus: WorkSessionStatus;
  workerName: string;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  projectId: string;
  projectSlug: string;
  projectName: string;
  /** Vyřešení (worker přes POST /api/worker/blockers/:id/resolve) — null = otevřená. */
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNote: string | null;
  /**
   * Kolikrát byla TÁŽ překážka nahlášená (shluk podle otisku textu, stejně
   * jako v context package). Pětkrát hlášená překážka je jiný signál než
   * jednorázová — bez tohohle čísla to ze seznamu nejde poznat.
   */
  occurrences: number;
  /** ID všech výskytů shluku — odbavuje se celá skupina, ne jen první. */
  occurrenceIds: string[];
  /** Kdy byla nahlášená poprvé (`createdAt` je nejnovější výskyt). */
  firstReportedAt: string;
}

/**
 * Obchvat workera (log kind WORKAROUND) — co neudělal/obešel, ale task dokončil.
 * Čte se seskupeně: zajímavé je, co se **opakuje**, protože to je kandidát na
 * úpravu AppFactory nebo AppFactoryWorker.
 */
export interface WorkerWorkaroundDTO {
  id: string;
  text: string;
  /** Co by to příště nevyžadovalo — vyplňuje worker. */
  whatWouldHelp: string | null;
  createdAt: string;
  sessionId: string;
  workerName: string;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  projectId: string;
  projectSlug: string;
  projectName: string;
  /** Kolikrát byl týž obchvat nahlášen (shluk podle otisku). */
  occurrences: number;
  occurrenceIds: string[];
  firstReportedAt: string;
}

/** Minimální Context Package pro worker (spec §51). */
export interface ContextPackageDTO {
  session: { id: string; leaseExpiresAt: string; heartbeatSeconds: number };
  worker: { name: string; capabilities: string[] };
  role: { department: string | null; basePrompt: string; workflow: string; modelHint: string | null } | null;
  constitution: string[];
  policies: ProjectPolicyDTO[];
  project: { slug: string; name: string; lifecycle: ProjectLifecycle; description: string };
  task: {
    number: number;
    title: string;
    description: string;
    priority: TaskPriority;
    status: string;
    acceptance: AcceptanceCriterion[];
    plan: PlanStep[];
    deadline: string | null;
    milestone: string | null;
  };
  parent: TaskRefDTO | null;
  subtasks: TaskRefDTO[];
  dependencies: (TaskRefDTO & { done: boolean })[];
  applications: { slug: string; name: string }[];
  /**
   * Závazné designy — aktuální design verze aplikací projektu (ZIP z Claude
   * Design). Pokud existují, worker z nich MUSÍ vycházet a nesmí vymýšlet
   * vlastní design. Obsah přes GET sessions/:id/uploads/:uploadId.
   */
  designs: {
    application: { slug: string; name: string };
    uploadId: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    version: number;
    label: string | null;
  }[];
  codebases: {
    slug: string;
    repoUrl: string | null;
    defaultBranch: string;
    devBranch: string | null;
    branchPolicy: string | null;
    prPolicy: string | null;
    testPolicy: string | null;
    dodChecks: DodCheck[];
  }[];
  /**
   * Všechny codebases projektu (`codebases` výše je jen podmnožina
   * nalinkovaná na task — může být prázdná). Jediný platný zdroj slugů:
   * všude, kde API chce codebaseSlug (deploy-config, codebase-repo, tests),
   * se používají slugy odsud — vymyšlený slug server odmítne.
   */
  projectCodebases: {
    slug: string;
    name: string;
    repoUrl: string | null;
    defaultBranch: string;
    devBranch: string | null;
    applications: { slug: string; name: string }[];
  }[];
  /** Definition of Done — sjednocené checky napojených codebases (spec §53). */
  dodChecks: DodCheck[];
  /**
   * Integrace projektu (spec §57/§58): worker dostává akce a režimy,
   * nikdy credentials. DENIED akce se neposílají.
   */
  connections: {
    key: string;
    name: string;
    status: ConnectionStatus;
    capabilities: { action: string; mode: "AUTO" | "APPROVAL" }[];
  }[];
  /** Index Knowledge — updatedAt umožňuje levný diff (co se změnilo od revize Plánu vývoje). */
  knowledge: { folder: string; slug: string; title: string; updatedAt: string; content?: string }[];
  /** Raw podklady projektu (spec §18) — obsah přes GET sessions/:id/uploads/:uploadId. */
  uploads: { id: string; filename: string; mimeType: string; sizeBytes: number; status: UploadStatus; note: string | null }[];
  /**
   * Environmenty projektu s deploy konvencí: `branch` je git větev environmentu.
   * Deploy na environment = dostat kód do jeho větve, bez pull requestů:
   * worker pushuje všechnu práci přímo do staging větve (typicky `beta`;
   * založí ji z `main`, když chybí) a každý push nasadí staging. Produkci
   * nasazuje owner ručně mergem `beta` → `main` — worker nikdy.
   */
  environments: {
    key: string;
    name: string;
    kind: EnvironmentKind;
    url: string | null;
    branch: string | null;
    deployPolicy: PolicyMode;
    /**
     * Kde environment běží. COOLIFY/RAILWAY = sledované; ZEABUR = ještě
     * nepřestěhované (stěhuje owner, worker na infrastrukturu nesahá);
     * null = nic nestojí, worker ho má založit na Coolify a nahlásit přes
     * deploy-config.
     */
    hostingProvider: HostingProvider | null;
    /**
     * Obecný hosting mapping (Coolify i Railway). projectId/environmentId
     * null = nenahlášeno; u ZEABUR vždy null (zeaburská ID nejsou použitelná).
     */
    hosting: {
      provider: HostingProvider | null;
      /**
       * Coolify server projektu (název + URL API). Token k němu runner
       * vloží do COOLIFY_API_TOKEN — do kontextu nepatří.
       */
      server: { name: string; apiUrl: string } | null;
      /** Railway project ID / Coolify project uuid. */
      projectId: string | null;
      /** Railway environment ID / název prostředí v Coolify. */
      environmentId: string | null;
      /** Nahlášené služby (codebase → Railway service / Coolify aplikace). */
      services: { codebaseSlug: string; serviceId: string; url: string | null }[];
    };
    /** Railway project ID; null = nenahlášeno. Zastaralé — čti `hosting`. */
    railwayProjectId: string | null;
    /** Railway environment ID uvnitř projektu; null = nenahlášeno. */
    railwayEnvironmentId: string | null;
    /** Už nahlášené Railway services (codebase → service) — co tu není, není zaregistrované. */
    railwayServices: { codebaseSlug: string; railwayServiceId: string }[];
  }[];
  /**
   * Poslední nasazení každé služby (spec §54). Po pushi je tohle jediný
   * spolehlivý způsob, jak poznat, co se s buildem stalo — build stamp na URL
   * říká jen „běží starý commit" a nerozliší „ještě se staví" od „spadlo".
   * U statusu FAILED si worker přečte důvod přes
   * GET sessions/:id/deployments/:deploymentId/logs.
   */
  lastDeployments: {
    deploymentId: string;
    environmentKey: string;
    codebaseSlug: string | null;
    status: string;
    commitSha: string | null;
    branch: string | null;
    deployedAt: string | null;
    finishedAt: string | null;
  }[];
  /**
   * Pitch Perfect (UI review smyčka): stav obrazovek projektu. APPROVED
   * obrazovky se už znovu nescreenshotují; CHANGES_REQUESTED mají reviewNote.
   * Upload přes POST sessions/:id/screens, detail přes GET sessions/:id/screens.
   * Výjimka: obrazovka, jejíž skutečná podoba se od posledního screenshotu
   * změnila, se nahrazuje novou verzí s polem uiChanged=true (i APPROVED —
   * status se vrátí na PENDING pro nové review).
   */
  screens: { key: string; title: string; status: UiScreenStatus; reviewNote: string | null }[];
  /** Počet nezpracovaných připomínek (screeny + feedback) — detail přes GET sessions/:id/feedback. */
  pendingFeedbackCount: number;
  /**
   * Tasky projektu čekající v QA gate (spec §53) — QA/tester session je
   * uzavírá přes POST sessions/:id/qa-verdict s taskId. Prázdné = nic nečeká.
   */
  qaQueue: {
    id: string;
    number: number;
    title: string;
    status: string;
    acceptance: AcceptanceCriterion[];
    reworkCount: number;
    qaNote: string | null;
  }[];
  /**
   * Neodškrtnuté položky `Product/Plán vývoje` se stavem tasků, na které se
   * odvolávají — posílá se jen kolu „Doplnit plán práce". Bez toho nemá kolo
   * jak ověřit, že položka „hotovo, čeká na QA" už QA prošla, a plán se nikdy
   * nedotáhne do konce.
   */
  planRefs?: { text: string; tasks: { number: number; status: string; qaVerdict: string | null }[] }[];
  recentDecisions: { title: string; resolution: string | null; status: DecisionStatus }[];
  /**
   * Otevřené blokace projektu (kind BLOCKER, nevyřešené) — povinná revize:
   * když worker ověří, že překážka pominula (typicky ji sám právě odstranil),
   * odbaví ji přes POST /api/worker/blockers/:id/resolve s note jak ověřil.
   */
  /**
   * Otevřené blokace projektu. Opakovaná hlášení téže překážky jsou sloučená
   * do jedné položky: `id` je nejstarší výskyt, `ids` všechny (odbavuj všechny),
   * `count` kolikrát se překážka nahlásila. Vysoké `count` znamená, že se
   * problém vrací a nikdo ho neodbavil — ne že je jistější.
   */
  openBlockers: {
    id: string;
    ids: string[];
    count: number;
    text: string;
    createdAt: string;
    lastReportedAt: string;
    taskNumber: number;
    taskTitle: string;
    workerName: string;
  }[];
  /**
   * Úkoly pro člověka na tomhle projektu. `OPEN` znamená „čeká se na někoho,
   * kdo se na to podívá" — nezakládej duplicitní. `finding` u nedávno
   * uzavřených je ODPOVĚĎ, na kterou jsi čekal: je to vstup do práce, ne
   * potvrzení, že se něco stalo (u kontroly tisku je nález přímo obsahem AC).
   */
  humanTasks: {
    id: string;
    title: string;
    status: HumanTaskStatus;
    urgency: DecisionUrgency;
    expectedFinding: string;
    /** Vyplněné jen u uzavřených — co člověk zjistil, doslova. */
    finding: string | null;
    blocksProject: boolean;
    createdAt: string;
    resolvedAt: string | null;
  }[];
  lastHandoff: SessionHandoff | null;
  discussion: { actorLabel: string; text: string; createdAt: string }[];
  /**
   * Seznamy, které se nevešly do rozpočtu balíku — klíč → celkový počet
   * položek v projektu. Balík sedí v kontextu session a čte se znovu při
   * každém kroku modelu, takže rostoucí seznamy mají strop. Prázdný objekt
   * znamená, že je balík úplný; je-li klíč přítomen, je seznam výřez a
   * zbytek si worker dotáhne cíleně (Knowledge přes
   * `GET …/knowledge/:folder/:slug`, obrazovky přes `GET …/screens`,
   * QA frontu dalším testovacím kolem).
   */
  truncated: {
    knowledge?: number;
    uploads?: number;
    screens?: number;
    qaQueue?: number;
    /** Diskuze tasku je výřez nebo zkrácená — celé znění přes `GET …/sessions/:id/discussion`. */
    discussion?: number;
  };
}

export interface TestRunDTO {
  id: string;
  taskId: string;
  sessionId: string | null;
  checkKey: string | null;
  codebase: string | null;
  name: string;
  status: TestStatus;
  detail: string | null;
  statsTotal: number | null;
  statsPassed: number | null;
  createdAt: string;
}

export interface ModelAnalyticsRowDTO {
  model: string;
  sessions: number;
  tokensTotal: number;
  completed: number;
  failed: number;
}

/** Factory Plan (spec §36) — dynamický pohled, ne entita. */
export interface FactoryPlanDTO {
  computedAt: string;
  now: TaskDTO[];
  next: TaskDTO[];
  then: TaskDTO[];
  queued: TaskDTO[];
  blockedCount: number;
}

// ---------------------------------------------------------------------------
// Fáze 6 — Routines & autonomie
// ---------------------------------------------------------------------------

export const RoutineStatus = z.enum(["ACTIVE", "PAUSED"]);
export type RoutineStatus = z.infer<typeof RoutineStatus>;

export const RoutineRunStatus = z.enum(["RUNNING", "COMPLETED", "FAILED"]);
export type RoutineRunStatus = z.infer<typeof RoutineRunStatus>;

/** Co smí routine vytvářet (spec §38). Incidenty přibudou ve Fázi 7. */
export const RoutineOutputKind = z.enum(["TASK", "PROPOSAL", "DECISION", "REPORT", "INCIDENT"]);
export type RoutineOutputKind = z.infer<typeof RoutineOutputKind>;

/**
 * Plán routine (spec §38). Časy jsou lokální čas serveru ("HH:MM").
 * INTERVAL: každých N minut; DAILY: každý den v time; WEEKLY: weekday (0=Ne) v time.
 */
export const ScheduleConfig = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("INTERVAL"), minutes: z.number().int().min(1).max(7 * 24 * 60) }),
  z.object({ kind: z.literal("DAILY"), time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) }),
  z.object({
    kind: z.literal("WEEKLY"),
    weekday: z.number().int().min(0).max(6),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  }),
]);
export type ScheduleConfig = z.infer<typeof ScheduleConfig>;

/** Reference výstupu běhu — vazba na vytvořenou entitu. */
export interface RoutineOutputRef {
  kind: RoutineOutputKind;
  id: string | null;
  label: string;
}

export interface RoutineRunDTO {
  id: string;
  routineId: string;
  workerId: string | null;
  workerName: string | null;
  status: RoutineRunStatus;
  startedAt: string;
  endedAt: string | null;
  summary: string;
  outputRefs: RoutineOutputRef[];
  report: string | null;
  models: string[];
  tokensTotal: number | null;
}

export interface RoutineDTO {
  id: string;
  name: string;
  departmentId: string | null;
  departmentName: string | null;
  departmentKey: string | null;
  schedule: ScheduleConfig;
  /** Lidsky čitelný popis plánu ("denně 07:00", "každých 5 min"). */
  scheduleLabel: string;
  instructions: string;
  autonomy: string;
  outputs: RoutineOutputKind[];
  capabilities: string[];
  modelHint: string | null;
  status: RoutineStatus;
  /** Scope — prázdné pole = všechny aktivní projekty. */
  projects: { id: string; slug: string; name: string }[];
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastRun: RoutineRunDTO | null;
  /** Součet tokenů dokončených běhů (spec §38: token usage). */
  tokensTotal: number;
  createdAt: string;
}

/** Context package běhu routine (spec §39/§51) — menší než task package. */
export interface RoutineContextDTO {
  run: { id: string; expiresAt: string };
  worker: { name: string; capabilities: string[] };
  routine: {
    name: string;
    scheduleLabel: string;
    instructions: string;
    autonomy: string;
    outputs: RoutineOutputKind[];
    modelHint: string | null;
  };
  role: { department: string | null; basePrompt: string } | null;
  constitution: string[];
  /** Projekty ve scope: krátký přehled, ne celé projekty. */
  projects: {
    id: string;
    slug: string;
    name: string;
    lifecycle: ProjectLifecycle;
    statusLine: string | null;
    attentionScore: number;
    openTaskCount: number;
    milestone: { title: string; deadline: string | null; atRisk: boolean } | null;
  }[];
  lastRun: { summary: string; endedAt: string | null } | null;
}

// ---------------------------------------------------------------------------
// Fáze 7 — Connections & Incidents
// ---------------------------------------------------------------------------

export const ConnectionStatus = z.enum(["CONNECTED", "MISSING", "ERROR"]);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

export const ConnectionGroup = z.enum(["DEVELOPMENT", "ANALYTICS", "BUSINESS", "OTHER"]);
export type ConnectionGroup = z.infer<typeof ConnectionGroup>;

/** Řádek capability matice (spec §57): akce → režim autonomie. */
export const CapabilityRow = z.object({
  action: z.string().trim().min(1).max(120),
  mode: PolicyMode,
  note: z.string().trim().max(200).default(""),
});
export type CapabilityRow = z.infer<typeof CapabilityRow>;

/** Coolify server (bez tokenu — jen hint). */
export interface HostingServerDTO {
  id: string;
  provider: HostingProvider;
  name: string;
  apiUrl: string;
  /** Poslední 4 znaky uloženého tokenu; "" = token chybí. */
  tokenHint: string;
  isDefault: boolean;
  owner: { id: string; name: string } | null;
  note: string;
  lastCheckedAt: string | null;
  lastError: string | null;
  /** Projekty, které na serveru běží (jen ty, které volající vidí). */
  projects: { id: string; slug: string; name: string }[];
  /** Smí volající server upravit a přiřazovat (vlastník nebo OWNER). */
  canManage: boolean;
}

/** Hosting server projektu + co z toho smí volající vybrat. */
export interface ProjectHostingServerDTO {
  /** Přiřazený server; null = výchozí. */
  server: Pick<HostingServerDTO, "id" | "name" | "apiUrl"> | null;
  /** Výchozí server Factory (null = není nastavený). */
  defaultServer: Pick<HostingServerDTO, "id" | "name" | "apiUrl"> | null;
  /** Servery, které smí volající projektu přiřadit. */
  assignable: Pick<HostingServerDTO, "id" | "name" | "apiUrl">[];
  canChange: boolean;
}

export interface ConnectionDTO {
  id: string;
  projectId: string;
  key: string;
  name: string;
  icon: string;
  group: ConnectionGroup;
  status: ConnectionStatus;
  capabilities: CapabilityRow[];
  departmentsNote: string;
  note: string;
  /** Jen názvy + hint — hodnoty nikdy neopouštějí server (spec §58). */
  secrets: { name: string; hint: string; updatedAt: string }[];
  lastCheckedAt: string | null;
  lastError: string | null;
  createdAt: string;
}

/** Výchozí šablony integrací s maticí dle /Design (spec §57). */
export interface ConnectionTemplate {
  key: string;
  name: string;
  icon: string;
  group: ConnectionGroup;
  departmentsNote: string;
  capabilities: CapabilityRow[];
}

export const CONNECTION_TEMPLATES: ConnectionTemplate[] = [
  {
    key: "github", name: "GitHub", icon: "⌥", group: "DEVELOPMENT",
    departmentsNote: "Development · QA · Security",
    capabilities: [
      { action: "číst repository / kód", mode: "AUTO", note: "základ pro každý DEV task" },
      { action: "vytvořit branch", mode: "AUTO", note: "založení staging větve (beta), když chybí" },
      { action: "commit + push", mode: "AUTO", note: "vývoj přímo do staging větve (beta)" },
      { action: "otevřít / mergovat PR", mode: "DENIED", note: "PR flow se nepoužívá — push přímo do beta" },
      { action: "merge beta → main", mode: "DENIED", note: "produkci merguje owner ručně" },
      { action: "force-push · mazání branchí", mode: "DENIED", note: "destruktivní — Constitution" },
      { action: "měnit repo settings / přístupy", mode: "DENIED", note: "pouze owner ručně" },
    ],
  },
  {
    key: "railway", name: "Railway", icon: "▲", group: "DEVELOPMENT",
    departmentsNote: "Development · Operations",
    capabilities: [
      { action: "číst stav služeb", mode: "AUTO", note: "monitoring" },
      { action: "deploy staging", mode: "AUTO", note: "automaticky každým pushem do beta" },
      { action: "deploy production", mode: "DENIED", note: "produkce = owner merge beta → main" },
      { action: "rollback production", mode: "APPROVAL", note: "i při incidentu" },
      { action: "smazat službu / databázi", mode: "DENIED", note: "destruktivní — Constitution" },
    ],
  },
  {
    key: "sentry", name: "Sentry", icon: "◉", group: "DEVELOPMENT",
    departmentsNote: "Operations · Development",
    capabilities: [
      { action: "číst errory a trendy", mode: "AUTO", note: "monitoring routine" },
      { action: "vytvořit incident ve Factory", mode: "AUTO", note: "dle severity matice" },
      { action: "resolve issue", mode: "AUTO", note: "po ověřené opravě" },
      { action: "měnit alert pravidla", mode: "APPROVAL", note: "mění chování monitoringu" },
    ],
  },
  {
    key: "ga4", name: "GA4", icon: "∿", group: "ANALYTICS",
    departmentsNote: "Analytics · Marketing · Product",
    capabilities: [
      { action: "číst analytiku", mode: "AUTO", note: "Analytics + Marketing" },
      { action: "vytvářet reporty", mode: "AUTO", note: "routine výstupy" },
      { action: "měnit konfiguraci measurementu", mode: "APPROVAL", note: "ovlivňuje data" },
    ],
  },
  {
    key: "search-console", name: "Search Console", icon: "⌕", group: "ANALYTICS",
    departmentsNote: "Content · SEO",
    capabilities: [{ action: "číst search výkon", mode: "AUTO", note: "SEO reporting" }],
  },
  {
    key: "stripe", name: "Stripe", icon: "$", group: "BUSINESS",
    departmentsNote: "Business · Support (read)",
    capabilities: [
      { action: "číst revenue / MRR", mode: "AUTO", note: "pro Business reporting" },
      { action: "číst zákazníky a subskripce", mode: "AUTO", note: "bez platebních údajů" },
      { action: "vytvořit refund", mode: "APPROVAL", note: "finanční dopad" },
      { action: "změnit cenu / plán", mode: "DENIED", note: "pouze owner — pricing je decision" },
      { action: "export dat", mode: "APPROVAL", note: "GDPR ohled" },
    ],
  },
  {
    key: "email", name: "E-mail / helpdesk", icon: "✉", group: "BUSINESS",
    departmentsNote: "Support",
    capabilities: [
      { action: "číst příchozí poštu", mode: "AUTO", note: "Support routine" },
      { action: "připravit draft odpovědi", mode: "AUTO", note: "vždy povoleno" },
      { action: "odeslat odpověď", mode: "APPROVAL", note: "per projekt — u klientů vždy" },
      { action: "smazat / přeposlat mimo firmu", mode: "DENIED", note: "ochrana dat" },
    ],
  },
];

export const IncidentSeverity = z.enum(["P0", "P1", "P2", "P3"]);
export type IncidentSeverity = z.infer<typeof IncidentSeverity>;

export const IncidentStatus = z.enum(["OPEN", "MONITORING", "RESOLVED"]);
export type IncidentStatus = z.infer<typeof IncidentStatus>;

export const IncidentSource = z.enum(["SENTRY", "MONITORING", "MANUAL", "ROUTINE", "WORKER"]);
export type IncidentSource = z.infer<typeof IncidentSource>;

export interface IncidentEventDTO {
  id: string;
  at: string;
  title: string;
  detail: string;
  kind: "INFO" | "ALERT" | "ACTION" | "RESOLVED";
}

export interface IncidentDTO {
  id: string;
  projectId: string;
  projectSlug: string;
  projectName: string;
  number: number;
  /** Zobrazované označení "#P1-112". */
  label: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  source: IncidentSource;
  summary: string;
  errorRate: string | null;
  affectedUsers: string | null;
  businessImpact: string | null;
  departmentKey: string | null;
  hotfixTask: (TaskRefDTO & { epScore: number }) | null;
  linkedDecisionId: string | null;
  startedAt: string;
  resolvedAt: string | null;
  resolutionNote: string;
  postMortemTaskNumber: number | null;
  durationMinutes: number;
  events: IncidentEventDTO[];
}

// ---------------------------------------------------------------------------
// Fáze 8 — Jarvis
// ---------------------------------------------------------------------------

/** Zpráva konverzace (klient drží historii, server je stateless — spec §65). */
export interface JarvisMessage {
  role: "user" | "assistant";
  content: string;
}

/** Pokyn UI, kam se má podívat (context-aware navigace, spec §71). */
export interface JarvisNavigate {
  /** Cesta v aplikaci, např. "/projects/rezervo" nebo "/incidents/<id>". */
  to: string;
  /** Volitelné zvýraznění: blockers | milestone | decisions (◉ JARVIS UKAZUJE). */
  highlight?: "blockers" | "milestone" | "decisions" | null;
  label: string;
}

/** Akce čekající na explicitní potvrzení (risk confirmation, spec §70). */
export interface JarvisPendingAction {
  tool: string;
  args: Record<string, unknown>;
  /** Text na potvrzovací tlačítko („POTVRDIT PRODUCTION DEPLOY"). */
  confirmLabel: string;
  reason: string;
}

export interface JarvisReplyDTO {
  reply: string;
  /** Provedené akce („DOPAD PROVEDENÝ FACTORY"). */
  impact: string[];
  navigate: JarvisNavigate | null;
  pendingAction: JarvisPendingAction | null;
  /** Stavová chip pod odpovědí („→ OTEVÍRÁM REZERVO…", „✓ PROVEDENO · AUDIT"). */
  chip: string | null;
}

export interface JarvisStatusDTO {
  /** LLM dostupné (OpenRouter klíč nebo fake režim). */
  online: boolean;
  model: string;
  /** ElevenLabs STT/TTS k dispozici. */
  voice: boolean;
}

// ---------------------------------------------------------------------------
// Pitch Perfect — QA smyčka nad screenshoty UI + projektový feedback
// ---------------------------------------------------------------------------

/** Stav obrazovky v review smyčce (spec: Pitch Perfect). */
export const UiScreenStatus = z.enum(["PENDING", "APPROVED", "CHANGES_REQUESTED"]);
export type UiScreenStatus = z.infer<typeof UiScreenStatus>;

export const UiScreenKind = z.enum(["PAGE", "MODAL", "FORM", "EMAIL", "COMPONENT", "OTHER"]);
export type UiScreenKind = z.infer<typeof UiScreenKind>;

/** Viewport screenshotu — obrazovky se odevzdávají ve třech velikostech. */
export const UiViewport = z.enum(["DESKTOP", "MOBILE", "TABLET"]);
export type UiViewport = z.infer<typeof UiViewport>;

/** Druhy obrazovek, u kterých je trojice viewportů povinná (responzivní UI). */
export const RESPONSIVE_SCREEN_KINDS: UiScreenKind[] = ["PAGE", "MODAL", "FORM"];

/** Viewporty, které musí mít každá responzivní obrazovka nascreenshotované. */
export const REQUIRED_VIEWPORTS: UiViewport[] = ["DESKTOP", "MOBILE", "TABLET"];

/** Reference tasku vytvořeného z připomínky: ukládá se do JSONB. */
export const FeedbackTaskRef = z.object({
  id: z.string(),
  number: z.number().int(),
  title: z.string(),
});
export type FeedbackTaskRef = z.infer<typeof FeedbackTaskRef>;

/** Připomínka k jednomu viewportu obrazovky (desktop OK, mobil ne): JSONB. */
export const ScreenViewportNote = z.object({
  viewport: UiViewport,
  note: z.string(),
});
export type ScreenViewportNote = z.infer<typeof ScreenViewportNote>;

export interface UiScreenshotDTO {
  id: string;
  version: number;
  viewport: UiViewport;
  mimeType: string;
  sizeBytes: number;
  note: string | null;
  createdByLabel: string;
  createdAt: string;
}

export interface UiScreenDTO {
  id: string;
  projectId: string;
  applicationId: string | null;
  applicationSlug: string | null;
  applicationName: string | null;
  key: string;
  title: string;
  kind: UiScreenKind;
  routePath: string | null;
  status: UiScreenStatus;
  reviewNote: string | null;
  /** Připomínky k jednotlivým viewportům (u CHANGES_REQUESTED). */
  reviewViewportNotes: ScreenViewportNote[];
  reviewedByLabel: string | null;
  reviewedAt: string | null;
  /** null = připomínka čeká na zpracování workerem. */
  feedbackProcessedAt: string | null;
  feedbackTaskRefs: FeedbackTaskRef[];
  /** Tasky, v jejichž sessions vznikly screenshoty (odvozeno přes sessionId). */
  originTaskRefs: FeedbackTaskRef[];
  latestShot: UiScreenshotDTO | null;
  /** Nejnovější screenshot pro každý viewport (pořadí dle REQUIRED_VIEWPORTS). */
  latestShots: UiScreenshotDTO[];
  shotCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface UiScreensSummaryDTO {
  total: number;
  approved: number;
  pending: number;
  changesRequested: number;
}

export interface ProjectFeedbackDTO {
  id: string;
  projectId: string;
  authorLabel: string;
  text: string;
  /** Příloha (obrázek) — obsah přes GET /api/projects/:id/feedback/:feedbackId/attachment. */
  hasAttachment: boolean;
  attachmentFilename: string | null;
  attachmentMimeType: string | null;
  processedAt: string | null;
  processedNote: string | null;
  taskRefs: FeedbackTaskRef[];
  createdAt: string;
}

/**
 * Osobní API token uživatele (MCP konektor, skripty). Samotný token se vrací
 * jedinkrát při vydání — tady je jen to, podle čeho ho poznáš v seznamu.
 */
export interface ApiTokenDTO {
  id: string;
  name: string;
  /** Poslední 4 znaky tokenu (afp_…a1b2). */
  hint: string;
  /** true = token vydal OAuth konektor, false = vydán ručně v Nastavení. */
  connector: boolean;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}
